import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Empty, Table, Tag } from "antd";
import dayjs from "dayjs";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetAdaptersSummaryQuery } from "../../../redux/adapters/adapterAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
import FilterSelectLiftType from "../../components/filters/FilterSelectLiftType";
import FilterSelectProtocolType from "../../components/filters/FilterSelectProtocolType";
import FilterSelectAuthType from "../../components/filters/FilterSelectAuthType";
import { buildFilterString, getTableRowClass } from "../../../utils/utils";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import TableColumnLiftType from "../../components/table/TableColumnLiftType";
import TableColumnAuthType from "../../components/table/TableColumnAuthType";
import { formatUuidT, adapterProtocolLabel } from "../../../flatbuffers/display";
import AdapterVersionHeader from "./AdapterVersionHeader";
import AdaptersTableActions from "./AdaptersTableActions";
// INTERFACES
import { AdapterInterface } from "../../../interfaces/Adapter.interface";
import { RootState } from "../../../app/store";

const PROTOCOL_TAG_PALETTE = [
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
] as const;

const getProtocolTagColor = (label: string): string => {
  const normalizedLabel = label.trim().toUpperCase();
  if (!normalizedLabel) {
    return "default";
  }

  let hash = 0;
  for (let i = 0; i < normalizedLabel.length; i += 1) {
    hash = (hash * 31 + normalizedLabel.charCodeAt(i)) % Number.MAX_SAFE_INTEGER;
  }

  return PROTOCOL_TAG_PALETTE[hash % PROTOCOL_TAG_PALETTE.length];
};

function AdaptersTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { Column } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [adapters, setAdapters] = useState<AdapterInterface[]>([]);
  const [filterName, setFilterName] = useState("");
  const [filterLiftType, setFilterLiftType] = useState("");
  const [filterAuthType, setFilterAuthType] = useState("");
  const [filterProtocolType, setFilterProtocolType] = useState("");
  const [filterCreatedAt, setFilterCreatedAt] = useState("");
  const [errorMessage, setErrorMessage] = useState<any>("");
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { adapterRefreshTable } = useSelector((state: RootState) => state.adapter);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetAdaptersTable,
    {
      data: dataGetAdapters,
      isLoading: isLoadingGetAdapters,
      isError: isErrorGetAdapters,
      isFetching: isFetchingGetAdapters,
      error: errorGetAdapters,
    },
  ] = useLazyGetAdaptersSummaryQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const getData = async (page: number, limit: number) => {
    const searchAndFilterString = buildFilterString({
      filterName,
      filterLiftTypeFullName: filterLiftType,
      filterAuthType,
      filterProtocolType,
      filterCreatedAt,
    });
    // if (userLogged && userLogged.organizationId && userLogged.organizationId !== globalOrganizationId) {
    // 	searchAndFilterString = searchAndFilterString.concat(`&org_id=${globalOrganizationId}`);
    // }
    const token = await GETJwtToken();
    triggerGetAdaptersTable({
      page,
      limit,
      token,
      searchAndFilterString,
    });
  };

  const handleTableChange = async (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  // Search keeps the typed filters and returns to page 1 at the current page size.
  const searchTable = async () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    getData(1, pagination.pageSize);
  };

  const refreshTable = async () => {
    setFilterName("");
    setFilterLiftType("");
    setFilterAuthType("");
    setFilterProtocolType("");
    setFilterCreatedAt("");
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (globalOrganizationId && globalOrganizationId !== "") {
      getData(pagination.current, pagination.pageSize);
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    if (dataGetAdapters && dataGetAdapters.data) {
      setPagination({
        ...pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: dataGetAdapters.total,
      });
      setAdapters(dataGetAdapters.data);
    }
  }, [dataGetAdapters]);

  useEffect(() => {
    if (adapterRefreshTable) {
      refreshTable();
    }
  }, [adapterRefreshTable]);

  useEffect(() => {
    if (errorGetAdapters) {
      setErrorMessage(errorGetAdapters);
      setAdapters([]);
      setPagination({
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        total: 0,
      });
    }
  }, [errorGetAdapters]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      {hasPermission(Permission.AdaptersView, permissionSet ?? []) ? (
        <Table
          locale={{
            emptyText: (
              <TableNoDataBox
                isError={isErrorGetAdapters}
                errorMessage={
                  errorMessage && errorMessage.data && errorMessage.data.message
                    ? errorMessage.data.message
                    : "Error"
                }
                noItemsFound="No adapters found"
              />
            ),
          }}
          rowClassName={(record, index) => `${getTableRowClass(index, theme)}`}
          className="my-custom-table"
          dataSource={adapters}
          loading={isLoadingGetAdapters || isFetchingGetAdapters}
          rowKey={(record: any) => formatUuidT(record.id)}
          size="small"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 900 }}
          footer={() => (
            <div className="generalStyles__flexEnd">
              <Tag>{pagination.total} Adapters</Tag>
            </div>
          )}
        >
          {/** NAME */}
          <Column
            title={t("adapter.name")}
            dataIndex="name"
            key="name"
            filterIcon={<FontAwesomeIcon icon={faSearch} />}
            filterDropdown={() => (
              <FilterSearchText
                placeHolder={t("general.name")}
                filterValue={filterName}
                setFilterValue={setFilterName}
                refreshTable={searchTable}
                refreshParameters={[]}
              />
            )}
            render={(text) => <div>{text}</div>}
          />
          {/** LIFT TYPE -- */}
          <Column
            title={`Lift ${t("edgeManagement.lifts.type")}`}
            dataIndex="liftType"
            key="liftType"
            filterIcon={<FontAwesomeIcon icon={faSearch} />}
            filterDropdown={() => (
              <FilterSelectLiftType
                placeHolder={t("general.liftType")}
                setFilterValue={setFilterLiftType}
                filterValue={filterLiftType}
                refreshTable={searchTable}
              />
            )}
            render={(text) => <TableColumnLiftType liftType={text ?? 0} />}
          />
          {/** PROTOCOL TYPE -- */}
          <Column
            title={t("general.protocol")}
            dataIndex="protocolType"
            key="protocolType"
            filterIcon={<FontAwesomeIcon icon={faSearch} />}
            filterDropdown={() => (
              <FilterSelectProtocolType
                placeHolder={t("general.protocol")}
                setFilterValue={setFilterProtocolType}
                filterValue={filterProtocolType}
                refreshTable={searchTable}
              />
            )}
            render={(text) => {
              const label =
                typeof text === "number" ? adapterProtocolLabel(text) : String(text ?? "");
              return (
                <Tag color={getProtocolTagColor(label)} className="drawer__typeTag">
                  {label || "--"}
                </Tag>
              );
            }}
          />
          {/** VERSION */}
          <Column
            title={<AdapterVersionHeader />}
            dataIndex="version"
            key="version"
            render={(text) => <div>{text || ""}</div>}
          />
          {/** AUTH TYPE */}
          <Column
            title={t("adapter.authType")}
            dataIndex="authType"
            key="authType"
            filterIcon={<FontAwesomeIcon icon={faSearch} />}
            filterDropdown={() => (
              <FilterSelectAuthType
                placeHolder={t("adapter.authType")}
                setFilterValue={setFilterAuthType}
                filterValue={filterAuthType}
                refreshTable={searchTable}
              />
            )}
            render={(text) => <TableColumnAuthType authType={text ?? 0} />}
          />
          {/** CREATED AT */}
          <Column
            title={t("general.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            render={(text: string | Uint8Array | null) => {
              const value = String(text ?? "").trim();
              if (!value) {
                return <Tag>--</Tag>;
              }

              const formatted = dayjs(value).isValid()
                ? dayjs(value).format("YYYY-MM-DD HH:mm")
                : value;

              return <Tag color="blue">{formatted}</Tag>;
            }}
          />
          {/** ACTIONS */}
          <Column
            title={t("adapter.actions")}
            key="action"
            align="center"
            onCell={() => ({
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
              },
            })}
            render={(_: any, record: AdapterInterface) => (
              <AdaptersTableActions selectedItem={record} />
            )}
          />
        </Table>
      ) : (
        <>
          <div className="generalStyles__noAccessToListTable">
            <Empty
              description={t("adapter.listNotAvailable")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default AdaptersTable;
